import moment from "moment";
import {
	debounce,
	find,
	filter,
	some,
	isEqual,
	uniq
} from "lodash";
import template from "./selection.html";
import "./selection.less";
import { customInputWarning } from "../prompts/customInputWarning";
import { entityFound } from "../prompts/entityFound";

import {
	alertDeactivatedSelection,
	notifyMultipleEntityTimezoneError,
	notifyNoEntityTimezoneError
} from "../../../manage-report/prompts";
import {
	EVENT_FILTER_PRIMARYDATE,
	EVENT_REPORT_SAVED,
	EVENT_FILTER_CURRENT_OPTIONS,
	EVENT_FILTER_ALL_OPTIONS,
	EVENT_VALUE_CHANGED,
	EVENT_REPORT_LOCKED,
	EVENT_REPORT_UNLOCKED,
	EVENT_SET_VALUE,
	EVENT_CLEAR_VALUE,
	EVENT_GET_VALUE,
	EVENT_SEND_VALUE,
	EVENT_TIMEZONE_SET,
	EVENT_HOTKEY_LOCK,
	EVENT_REVERT_VALUES,
	EVENT_HANDLE_SUCCESSOR_LOCK,
	EVENT_ENABLE_SUCCESSOR_LOCK,
	EVENT_DISABLE_SUCCESSOR_LOCK,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";
import fieldHelpers from "../../../field-helpers/lookup.config";
import {
	IS_NOT_AVAILABLE_OPTIONS,
	RELATIONSHIP_TYPES
} from "../../../../constants";
import { UNIQUE_TIMEZONE_ERRORS } from "../../../../services/entity-manager";
import { getSeasonsByEffectiveRanges } from "../../../../utils/getSeasonsByEffectiveRanges";
import { FORM_USER_FAVORITES } from "routes";
import { isStringNullishOrEmpty } from "../../../../services/string-service";
import { isValueNullish } from "../../../../utils/valueUtilities";
import { getFieldUsageDefault } from "../../../../utils/dataUsage";

// selection
export class Controller {
	options = [];
	lookups = [];
	selection = { primary: {}, friendly: {} };

	constructor(
		$scope,
		featureCallbacks,
		toastr,
		$timeout,
		$logger,
		appState,
		featureHelper,
		entityManager,
		api,
		$userProfile,
		loadingService,
		uiHelpers
	) {
		this._scope = $scope;
		this.featureCallbacks = featureCallbacks;
		this._toastr = toastr;
		this.$timeout = $timeout;
		this.$logger = $logger;
		this.appState = appState;
		this.featureHelper = featureHelper;
		this.entityManager = entityManager;
		this.api = api;
		this.$userProfile = $userProfile;
		this.loadingService = loadingService;
		this.runOnReady = [];
		this.uiHelpers = uiHelpers;
	}

	$onInit() {
		//when this is NOT used on the report page, the events/triggers are fairly pointless.
		this._eventBus = this.reportScope?.events ?? {
			register: () => {},
			trigger: () => {}
		};

		this.isFocused = false;

		this.textFieldId = `textInput-${this.field.Id}`;
		this.selectFieldId = `select-${this.field.Id}`;

		if(!this.subFieldCtrl) {
			this._unregister = this.reportFormCtrl && this.reportFormCtrl.registerFieldController(this);
		}
		else {
			this._unregister = this.subFieldCtrl(this);
		}

		this.initialValue = find(this.values, v => v.FieldId === this.field.Id);
		if(this.initialValue) {
			this.initialValue.IsNotAvailable = !!this.initialValue.IsNotAvailable;
			this.isNotAvailable = this.initialValue.IsNotAvailable;
		}
		this.loadFieldData();

		this.setAdvancedSearch();

		const self = this;
		this._scope.$watch(() => this.value, debounce((next) => {
			if(!isValueNullish(this.suppressMatchValue) && !isValueNullish(next)) {
				const isRestoredValue = next === this.suppressMatchValue;
				this.suppressMatchValue = undefined;
				if(isRestoredValue) {
					return;
				}
			}

			if(this.isCustomInput !== true || isValueNullish(next) || next === this.initialValue?.String) {
				return;
			}

			const match = this.findActiveDataItem(this.allOptionsIncludingUnfavorited, next);
			if(match) {
				let renderingSlots = this.uiHelpers.getDefaultEntitySlots(match, match.Entities?.[0]?.Entity ?? {}, this.field.IsCodeHidden, false);

				if(this.field.RenderingDirectives?.Display?.Selected) {
					renderingSlots = this.uiHelpers.getRenderingSlots(match, this.field.RenderingDirectives?.Display?.Selected);
				}

				entityFound(this.field, renderingSlots, this.$logger)
					.then(result => {
						if(!result?.dismiss) {
							const revertVal = this.value;
							this.setCustomInput(false);

							self.handleAdvancedSearchAction(match)
								.then(applied => {
									if(!applied) {
										this.activateCustomInput();
										this.suppressMatchValue = revertVal;
										this.value = revertVal;
										this.handleCustomInputChanged();

										return;
									}

									self.updateCustomInputState(false);
									self.enableHelpers();
									
									self.$timeout(function() {
										window.document.getElementById(self.selectFieldId).focus();
									});
								})
								.catch(() => {
									this._toastr.error(`An error occurred while adding the ${this.field.Name}.`);
								});
						}
					});
			}
		}, 200, {
			leading: false,
			trailing: true
		}));

		this.registerForEvents();
	}

	findActiveDataItem = (data, value) => { 
		const normalizedValue = value?.toLowerCase();
		if(!normalizedValue) {
			return undefined;
		}

		const collectMatches = (entities, parents = []) => (entities ?? []).reduce((matches, entity) => {
			if(!entity || entity.IsActive === false || this.isDeactivated(entity.DeactivatedAt)) {
				return matches;
			}

			const path = [...parents, entity];

			if(entity.Code?.toLowerCase() === normalizedValue) {
				matches.byCode.push(path);
			}
			else if((entity.Identifiers ?? []).some(identifier => identifier.Value?.toLowerCase() === normalizedValue)) {
				matches.byIdentifier.push(path);
			}

			if(entity.Entities?.length) {
				const childMatches = collectMatches(entity.Entities.map(e => e.Entity), path);
				matches.byCode.push(...childMatches.byCode);
				matches.byIdentifier.push(...childMatches.byIdentifier);
			}

			return matches;
		}, { byCode: [], byIdentifier: [] });

		const buildMatch = (matchPath) => {
			if(matchPath.length === 1) {
				return matchPath[0];
			}

			const matchedEntity = matchPath[matchPath.length - 1];
			const nestedMatch = matchPath.slice(1, -1).reduceRight((match, entity) => ({
				...entity,
				Entities: [{ Entity: match }]
			}), matchedEntity);

			return {
				...matchPath[0],
				Entities: [{ Entity: nestedMatch }],
				// an exact nested match resolves to this specific descendant, so the
				// confirm path can select it directly instead of re-running primary selection
				MatchedPrimary: matchedEntity
			};
		};

		const matches = collectMatches(data);

		// a single unique Code match is valid regardless of Identifier matches elsewhere
		if(matches.byCode.length === 1) {
			return buildMatch(matches.byCode[0]);
		}

		if(matches.byIdentifier.length === 1) {
			return buildMatch(matches.byIdentifier[0]);
		}

		return undefined;
	};

	isSelectionEligible = (selection) => {
		if(selection?.Id === undefined || selection?.Id === null) {
			return false;
		}

		const friendly = (this.allOptionsIncludingUnfavorited ?? []).find(item =>
			item.Id === selection.Id
			&& item.IsActive !== false
			&& !this.isDeactivated(item.DeactivatedAt)
		);
		if(!friendly) {
			return false;
		}

		if(!selection.MatchedPrimary) {
			return true;
		}

		const hasActivePrimary = (entities) => (entities ?? []).some(({ Entity }) => {
			if(!Entity || Entity.IsActive === false || this.isDeactivated(Entity.DeactivatedAt)) {
				return false;
			}

			return Entity.Id === selection.MatchedPrimary.Id || hasActivePrimary(Entity.Entities);
		});

		return hasActivePrimary(friendly.Entities);
	};

	isItemFavorited = (item) => {
		if(item.IsFavorite) {
			return true;
		}

		if(item.Entities?.length) {
			return item.Entities.some(e => this.isItemFavorited(e.Entity));
		}

		return false;
	};

	loadFieldData = () => {
		this.allOptionsIncludingUnfavorited = this.field.Data ?? [];

		if(this.field.IsFavoriteEnabled) {
			this.field.options = this.field.Data.filter(this.isItemFavorited);
			const initialValueInOptions = this.entityManager.findFriendlyByPrimaryId(this.field.options, this.initialValue?.DataId ?? this.initialValue?.EntityId);
			if(this.initialValue && initialValueInOptions === undefined && this.initialValue.String === undefined && !this.initialValue.IsNotAvailable) {
				const initialDataItem = this.entityManager.findFriendlyByPrimaryId(this.field.Data, this.initialValue?.DataId ?? this.initialValue?.EntityId);
				if(initialDataItem === undefined) {
					this.$logger.error("initial value is defined but not found in field data", { value: this.initialValue });
				}
				else {
					this.field.options.push(initialDataItem);
				}
			}
		}
		else {
			this.field.options = this.field.Data || [];
		}

		this.field.options.forEach(o => o.IsRecent = (o.IsRecent || this.hasDescendentRecent(o.Entities)));

		return this.field.options;
	};

	setupCustomEntryFeature() {
		if(this.field.IsCustomEntryEnabled) {
			const feature = this.featureHelper.getCustomInputFeature(this.field, this.isCustomInput, (selection) => this.setCustomInput(selection));
			this.lookups.unshift(feature);
		}
	}

	setupNotAvailableFeature(valueSet) {
		if(!this.isNotAvailableFeatureEnabled
			&& this.featureHelper.checkNotAvailable(this.field, IS_NOT_AVAILABLE_OPTIONS.always)
		) {
			const feature = this.featureHelper.getNotAvailableFeature(this.field, this.isNotAvailable || valueSet, (selection) => this.setNotAvailable(selection));
			this.lookups.push(feature);
			this.isNotAvailableFeatureEnabled = true;
		}
	}

	registerForEvents() {
		this._eventBus.register(EVENT_REPORT_SAVED, () => {
			if(this.field.IsRecentlyUsedEnabled) {
				this.selectBox.setChoices();
			}
		}, this._scope);

		this._eventBus.register(EVENT_FILTER_CURRENT_OPTIONS(this.field.Id), (e, params) => {
			if(this.field.isDisabled || (!params.readonly && this.isNotAvailable !== true)) {
				this.enableField();
			}

			this.setOptions(params);
		}, this._scope);

		this._eventBus.register(EVENT_FILTER_ALL_OPTIONS(this.field.Id), (e, params) => {
			if(this.field.isDisabled) {
				this.enableField();
			}

			this.setOptions(params);
		}, this._scope);

		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (e, params) => {
			this.setValue(params.value);
			this.clearSelected();

			if(this.selection.primary) {
				this.selectBox.setValue(this.selection.primary, this.selection.friendly);
			}
		}, this._scope);

		this._eventBus.register(EVENT_GET_VALUE(this.field.Id), (_, params) => {
			this._eventBus.trigger(EVENT_SEND_VALUE(params.id), {
				key: this.field.Key.Code,
				value: this.getCurrentValue()
			});
		}, this._scope);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), () => {
			const clearValue = () => {
				if(this.field.isDisabled) {
					this.field.isDisabled = true;
					this.disableField();
				}

				if(this.selection.isInitializedValue !== true) {
					this.clearSelected();
					this.setCurrentValue();
				}
			};

			if(this.isReady) {
				clearValue();
			}
			else {
				this.runOnReady.push(clearValue);
			}
		}, this._scope);

		this._eventBus.register(EVENT_REPORT_LOCKED, () => {
			this.field.isReadonly = true;
			this.disableField();
		}, this._scope);

		this._eventBus.register(EVENT_REPORT_UNLOCKED, () => {
			this.field.isReadonly = false;
			this.enableField();
		}, this._scope);

		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_HOTKEY_LOCK, () => this.isFocused && this.field.IsLockable && (this.field.isLocked = !this.field.isLocked), this._scope);

		this._eventBus.register(EVENT_HANDLE_SUCCESSOR_LOCK(this.field.Id), (e, params) => {
			this.field.isFieldLocakble = params.enable && this.field.IsLockable;
			if(this.field.isFieldLocakble !== true) {
				this.field.isLocked = false;
				this._eventBus.trigger(EVENT_DISABLE_SUCCESSOR_LOCK(this.field.Id));
			}
			else {
				this._eventBus.trigger(EVENT_ENABLE_SUCCESSOR_LOCK(this.field.Id));
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);

		if(this.field.hasEffectiveRanges) {
			this.registerPrimaryDateFilter();
		}
	}

	$onDestroy() {
		this._unregister && this._unregister();
	}

	enableField() {
		if(this.field.hasEffectiveRanges && this.effectiveAsOf === undefined && this.isCustomInput !== true) {
			return;
		}

		this.field.isDisabled = false;
		if(!this.field.isReadonly) {
			if(!this.isNotAvailable) {
				this.selectBox.enable();
				this.field.placeHolder = null;
			}
			this.$timeout(() => {
				this.enableHelpers();
				this.setHelpersBasedOnState();
			});
		}
	}

	disableField() {
		if(this.field.isReadonly || this.field.isDisabled) {
			this.selectBox.disable();
			this.disableHelpers();
		}
	}

	disableHelpers(exceptedName) {
		this.enableHelpers();
		this.lookups.forEach(l => {
			if(this.reportFormCtrl.readonly || !exceptedName || l.name !== exceptedName) {
				l.disabled = true;
			}
		});
	}

	enableHelpers() {
		this.lookups.forEach(l => l.disabled = false);
	}

	registerPrimaryDateFilter() {
		this._eventBus.register(EVENT_FILTER_PRIMARYDATE, (e, params) => {
			if(!this.isCustomInput) {
				if(this.isReady) {
					this.filterDataOnPrimaryDate(params.effectiveAsOf, params.autoSelect);
				}
				else {
					this.runOnReady.push(() => this.filterDataOnPrimaryDate(params.effectiveAsOf, params.autoSelect));
				}
			}
			else {
				this.effectiveAsOf = params.effectiveAsOf;
			}
		}, this._scope);
	}

	filterDataOnPrimaryDate = (effectiveAsOf, autoSelect, forceSelection) => {
		if(effectiveAsOf) {
			this.effectiveAsOf = effectiveAsOf;
		}

		const selection = forceSelection ?? this.selection;
		if(!selection?.primary?.Id && forceSelection === undefined) {
			this.autoSelectDefaultEntity(this.effectiveAsOf);
		}

		if(!selection?.primary) {
			return false;
		}

		this.featureCallbacks.nestedEntityDateFilter(selection.primary, this.effectiveAsOf, this.appState.forms.selected.Seasons);

		this.field.placeHolder = null;

		if(!this.initialValue?.EntityId) {
			this.enableField();
		}

		if(selection.primary.Id && selection.primary.valid !== true && selection.primary.inGrace !== true) {
			delete this.effectiveAsOf;
			if(autoSelect === false) {
				this._toastr.warning(
					`The currently selected ${this.field.EntityQueryConfiguration.Primary.Type.Name} is invalid for the selected date. Please select a new ${this.field.Name}.`,
					"Warning"
				);
				return false;
			}
			this.featureCallbacks.nestedEntityDateFilter(selection.friendly, this.effectiveAsOf, this.appState.forms.selected.Seasons);
			const validLicenses = selection.friendly.Entities.filter(e => e.Entity.valid);
			const graceLicenses = selection.friendly.Entities.filter(e => e.Entity.valid !== true && e.Entity.inGrace === true);
			if(validLicenses.length !== 1) {
				this.clearSelected();
				if(graceLicenses.length !== 1) {
					this.setCurrentValue();

					this._toastr.warning(
						`Unable to automatically select a ${this.field.EntityQueryConfiguration.Primary.Type.Name} for the selected date. Please select a ${this.field.Name}.`,
						"Warning"
					);
					return true;
				}

				this.setCurrentValue(graceLicenses[0].Entity, this.selection.friendly);
				this._toastr.warning(
					`Selected ${this.field.EntityQueryConfiguration.Primary.Type.Name} was invalid for the selected date.
						A ${this.field.EntityQueryConfiguration.Primary.Type.Name} currently in a grace period has been updated for the currently selected date.`
					, "Warning"
				);

				return true;
			}

			this.clearSelected();
			this.setCurrentValue(validLicenses[0].Entity, this.selection.friendly);

			this._toastr.warning(`Selected ${this.field.EntityQueryConfiguration.Primary.Type.Name} was invalid for the selected date.
				Selected ${this.field.EntityQueryConfiguration.Primary.Type.Name} has been updated for the currently selected date.`, "Warning");

			return true;
		}

		return false;
	}

	hasDescendentRecent = (primaries) => {
		const areChildren = primaries?.some(p => p.Entity !== undefined);
		if(!areChildren) {
			return false;
		}

		const entities = primaries.map(p => p.Entity);

		entities.forEach(p => p.IsRecent = (p.IsRecent || this.hasDescendentRecent(p.Entities)));

		return entities.some(p => p.IsRecent);
	};

	setOptions(params) {
		this.field.options = params?.ids ?
			filter(this.field.Data, (d) => some(params.ids, (i) => d.Id === i || d.Entities?.some(e => e.Entity.Id === i))) :
			this.loadFieldData();

		if(params?.ids) {
			this.allOptionsIncludingUnfavorited = this.field.options;
		}

		if(!this.isCustomInput && this.selection?.primary && !some(this.field.options, (o) => o.Id === this.selection?.primary?.Id || o.Id === this.selection?.friendly?.Id)) {
			this.clearSelected();
			this.setCurrentValue();
		}

		this.selectBox.setChoices(this.field.options);
	}

	clearSelected() {
		if(!this.isCustomInput && this.selectBox) {
			this.selectBox.clearSelected();
		}
		else {
			this.value = "";
		}
	}

	setAdvancedSearch() {
		if(!this.field.isSelf) {
			this.lookups.push({
				name: fieldHelpers.AdvancedSearch.name,
				fieldId: this.field.rootFieldId ?? this.field.Id,
				hideCode: this.field.IsCodeHidden,
				disabled: this.field.isDisabled || this.field.isReadonly,
				options: {
					isFavoriteEnabled: this.field.IsFavoriteEnabled,
					matchSpecificity: this.field.MatchSpecificity
				},
				action: this.handleAdvancedSearchAction
			});
		}
	}

	handleAdvancedSearchAction = async(selection) => {
		if(selection === undefined || !this.isSelectionEligible(selection)) {
			return false;
		}

		if(this.field.IsFavoriteEnabled && selection.IsFavorite !== true) {
			// set favorite on api here.  Currently we favorite the friendly entity.
			const targetType = this.field.EntityQueryConfiguration.Friendly.Type.Code;
			const relationshipType = "SELF";
			const setFavorite = this.api.post(FORM_USER_FAVORITES(
				this.appState.forms.selected.Id,
				this.$userProfile.getUser().databaseId,
				selection.Id,
				targetType,
				relationshipType
			));
			await this.loadingService.watch(setFavorite);

			const thisDataItem = this.field.Data.find(d => d.Id === selection.Id);
			thisDataItem.IsFavorite = true;

			this.entityManager.updateEntity(thisDataItem);
			const thisField = this.appState.forms.selected.Fields.find(f => f.Id === this.field.Id);
			const thisItemIndex = thisField.Data.findIndex(d => d.Id === thisDataItem.Id);
			thisField.Data[thisItemIndex] = thisDataItem;

			this.setOptions();
		}

		if(!this.isSelectionEligible(selection)) {
			return false;
		}

		const matchedPrimary = selection.MatchedPrimary;
		delete selection.MatchedPrimary;

		if(matchedPrimary) {
			// an exact nested match resolves to a specific descendant - select it
			// directly instead of re-running primary selection across all children
			this.selectionChanged({ primary: matchedPrimary, friendly: selection });
			return true;
		}

		this.selectBox.setDisplay(selection.Id);
		this.selectBox.checkForPrimary();

		return true;
	}

	getCurrentValue = (isApiCall = false) => {
		let result = this.buildResult();

		if(!isApiCall) {
			if(!result) {
				result = { FieldId: this.field.Id };
				result.hasResultForRuleRun = false;
			}
			result.isDirty = this.isDirty(result);

			const getClassifications = (friendly, primary) => {
				const classifications = [
					...(friendly && friendly.Classifications ? friendly.Classifications : []),
					...(friendly && friendly.Classification ? [friendly.Classification] : []),
					...(primary && primary.Classifications ? primary.Classifications : []),
					...(primary && primary.Classification ? [primary.Classification] : [])
				];

				return classifications;
			};

			const getSeasons = (entity) => {
				if(entity.Effective) {
					return getSeasonsByEffectiveRanges(entity.Effective, this.appState.forms.selected.Seasons);
				}
				return this.appState.forms.selected.Seasons;
			};

			if(!this.isCustomInput && !this.isNotAvailable) {
				const primary = this.selection?.primary;
				const friendly = this.selection?.friendly;

				if(primary?.Id) {
					result.Classifications = getClassifications(friendly, primary);
					result._code = primary.Code;
					result.allCodes = uniq([primary.Code, friendly?.Code]).filter(c => c !== undefined),
					result.Identifiers = (friendly?.Identifiers || []).concat(primary.Identifiers || []);
					result.Seasons = getSeasons(primary);
					result.externalKeys = uniq([primary.ExternalKey, friendly?.ExternalKey]).filter(c => c !== undefined);
					result.Shape = primary.Shape;
					result.Geometries = primary.Geometries ?? [];
				}
				else if(friendly?.Id) {
					result.Classifications = getClassifications(friendly);
					result._code = friendly.Code;
					result.allCodes = uniq([primary?.Code, friendly.Code]).filter(c => c !== undefined);
					result.Identifiers = (friendly.Identifiers || []);
					result.Seasons = getSeasons(friendly);
					result.externalKeys = uniq([primary?.ExternalKey, friendly.ExternalKey]).filter(c => c !== undefined);
					result.Shape = friendly.Shape;
					result.Geometries = friendly.Geometries ?? [];
				}
			}
		}

		return result ? [result] : [];
	}

	revertValue() {
		this.setValue(this.initialValue);
		this.selectBox.refresh();
	}

	buildResult() {
		if(this.isCustomInput === true && !isStringNullishOrEmpty(this.value)) {
			return {
				FieldId: this.field.Id,
				String: this.value
			};
		}
		else if(this.isNotAvailable === true) {
			return {
				FieldId: this.field.Id,
				IsNotAvailable: true
			};
		}

		const friendlyId = this.selection?.friendly?.Id;
		const primaryId = this.selection?.primary?.Id;

		return friendlyId || primaryId
			? {
				FieldId: this.field.Id,
				EntityId: primaryId || friendlyId
			} : undefined;
	}

	setPristine = () => {
		this.initialValue = this.buildResult();
	}

	setCurrentValue(primary, friendly, isInitializedValue) {
		const newSelection = {
			primary: primary || {},
			friendly: friendly || {}
		};

		if(isInitializedValue) {
			newSelection.isInitializedValue = true;
		}

		if(isEqual(this.selection, newSelection))
			return;

		this.selection = newSelection;

		this.selectBox.setValue(this.selection.primary, this.selection.friendly, true);

		this.handleValueChanged();
	}
	/**
	 *
	 * @param deactivatedDate {(string)}
	 * @returns
	 */

	isDeactivated = (deactivatedDate) => {
		return !!deactivatedDate && moment(deactivatedDate).isBefore(moment());
	}

	setSelectBox = (selectBox) => {
		this.selectBox = selectBox;

		if(this.featureHelper.checkNotAvailable(this.field, IS_NOT_AVAILABLE_OPTIONS.always)) {
			this.setNotAvailable(this.isNotAvailable, true);
		}

		this.setValue();

		if(!this.field.hasEffectiveRanges && this.field.options.length === 1) {
			this.autoSelectDefaultEntity();
		}

		this.setupCustomEntryFeature();
		this.setupNotAvailableFeature();
		this.setHelpersBasedOnState();

		this.isReady = true;
		this.runOnReady.forEach(func => func());
		this.runOnReady = [];
	};

	setHelpersBasedOnState() {
		if(this.isCustomInput) {
			this.disableHelpers(fieldHelpers.CustomInput.name);
		}
		else if(this.isNotAvailable) {
			this.disableHelpers(fieldHelpers.NotAvailable.name);
		}
		else if(this.field.isDisabled || this.field.isReadonly) {
			this.disableHelpers();
		}
	}

	setNotAvailable(selection, initializing = false) {
		if(selection !== undefined && selection === this.isNotAvailable) {
			return;
		}

		if(!this.selection) {
			this.selection = {};
		}

		this.isNotAvailable = selection;

		if(this.isNotAvailable) {
			this.clearSelected();
			this.selection = null;
			this.value = null;
			if(!this.isCustomInput) {
				this.box && this.box.disable();
			}
			this.field.isDisabled = true;
			!initializing && this.disableHelpers(fieldHelpers.NotAvailable.name);
		}
		else if(!this.field.isReadonly) {
			if(!this.isCustomInput) {
				this.box && this.box.enable();
				!initializing && this.enableHelpers();
			}
			this.field.isDisabled = false;
		}
	}

	setValue(value, skipRangeCheck) {
		const reportValue = value ? value : find(this.values, v => v.FieldId === this.field.Id);
		const isNewValue = this.field.IsDetail ? this.values === undefined : this.reportScope?.isNew;
		const usageDefault = isNewValue ? getFieldUsageDefault(this.field, this.field.options) : undefined;

		this.value = reportValue ?? (usageDefault && { EntityId: usageDefault.Id });

		let friendly, primary, isDefaultValue;

		if(!reportValue && usageDefault) {
			isDefaultValue = true;
		}

		if(this.field.options.length === 1
			&& !!this.field.options[0].Entities
			&& this.field.options[0].Entities.length === 1
			&& (!this.field.options[0].Entities[0].Entity
				|| !this.field.options[0].Entities[0].Entity.Effective
				|| skipRangeCheck === true)
		) {

			friendly = this.field.options[0];
			primary = {};
			if(!this.field.isSelf) {
				primary = friendly.Entities[0].Entity;
				isDefaultValue = true;
			}
		}

		if(this.value) {
			if(!!this.value.String && this.field.IsCustomEntryEnabled) {
				this.value = this.value.String;
				this.isCustomInput = true;
				this.updateCustomInputState(true);
				this.setCurrentValue({}, {});
				this.selectBox.clearSelected();
				this.lastCustomInputValue = this.value;
				
				return;
			}
			else if(this.field.isSelf) {
				friendly = find(this.field.options, (o) => {
					return o.Id == this.value.EntityId;
				}) || {};

				primary = {};
			}
			else {
				friendly = find(this.field.options, (o) => {
					return find(o.Entities, (e) => {
						return e.Entity.Id == this.value.EntityId;
					});
				}) || {};

				primary = (find(friendly.Entities, (e) => {
					return e.Entity.Id == this.value.EntityId;
				}) || {}).Entity || {};
			}
		}

		if(!friendly || this.isNotAvailable) {
			return;
		}

		this.setCurrentValue(primary, friendly, isDefaultValue);
	}

	autoSelectDefaultEntity(effectiveAsOf) {
		if(this.field.Data.length === 1) {
			const isSelf = this.field.EntityQueryConfiguration.RelationshipType.Code === RELATIONSHIP_TYPES.Self;
			const friendly = this.field.Data[0];

			let validEntities;
			let graceEntities;
			if(!!effectiveAsOf) {
				this.featureCallbacks.nestedEntityDateFilter(friendly, effectiveAsOf, this.appState.forms.selected.Seasons);
				if(isSelf) {
					validEntities = friendly.valid ? [friendly] : [];
					graceEntities = friendly.inGrace ? [friendly] : [];
				}
				else {
					validEntities = filter(friendly.Entities, (d) => d.Entity.valid);
					graceEntities = filter(friendly.Entities, (d) => d.Entity.inGrace);
				}
			}
			else {
				validEntities = isSelf
					? [friendly]
					: friendly.Entities;
			}

			if(validEntities && validEntities.length === 1) {
				if(this.selection?.primary) {
					this.clearSelected();
				}

				const primary = validEntities[0].Entity || {};
				this.selectBox.setValue(primary, friendly, true);
				!isSelf && this.setPrimarySwitcherButton(validEntities);
				this.selectionChanged({ primary, friendly }, true);
				this.value = primary;
			}
			else if(graceEntities && graceEntities.length === 1) {
				if(this.selection?.primary) {
					this.clearSelected();
				}

				const primary = graceEntities[0].Entity || {};
				this.selectBox.setValue(primary, friendly, true);
				!isSelf && this.setPrimarySwitcherButton(graceEntities);
				this.selectionChanged({ primary, friendly }, true);
				this.value = primary;
			}
		}
	}

	handleLookupSelection(lookup) {
		lookup.action(lookup.selection);
	}

	selectionChanged(selection, isInitializedValue) {
		if(selection) {
			if(this.isDeactivated(selection.primary.DeactivatedAt) || this.isDeactivated(selection.friendly.DeactivatedAt)) {
				alertDeactivatedSelection(selection.friendly.Name || selection.primary.Type.Name || "");
				this.setCurrentValue({}, {});
				this.clearSelected();
				return;
			}

			const wasUpdated = this.filterDataOnPrimaryDate(undefined, true, selection);

			if(!wasUpdated) {
				this.setCurrentValue(selection.primary, selection.friendly, isInitializedValue);
			}
		}
		else {
			this.setCurrentValue({}, {});
			this.clearSelected();
		}
	}

	setCustomInput(featureActive) {
		if(!featureActive) {
			this.selection = {};
		}

		if(featureActive) {
			this.showCustomInputWarning();
		}
		else {
			this.isCustomInput = false;
			this.enableHelpers();
			this.value = null;

			this.handleCustomInputChanged();
		}
	}

	async showCustomInputWarning() {
		const self = this;
		const userAction = await customInputWarning(this.$logger);

		if(userAction.dismiss) {
			this.updateCustomInputState(false);
			this.isCustomInput = false;

			this.$timeout(function() {
				window.document.getElementById(self.selectFieldId).focus();
			});
		} 
		else {
			this.activateCustomInput();
		}

		this._scope.$apply();
	}

	activateCustomInput() {
		this.clearSelected();
		this.isCustomInput = true;
		this.selection = null;
		this.value = "";
		this.disableHelpers(fieldHelpers.CustomInput.name);

		const self = this;

		this.lastCustomInputValue = null;

		this.$timeout(function() {
			const txt = window.document.getElementById(self.textFieldId);

			txt.focus();
			txt.select();
		});
	}

	updateCustomInputState(newState) {
		const inputLookup = find(this.lookups, (lookup) => lookup.name === fieldHelpers.CustomInput.name);
		if(inputLookup) {
			inputLookup.selection = newState;
		}
	}

	handleValueChanged() {
		const ctrl = this;

		const found = find(this.field.options, { Id: (this.selection.friendly || {}).Id });

		if(this.selection?.primary?.Id) {
			this.setPrimarySwitcherButton(found.Entities.filter((entity) => entity.Entity.valid));
		}
		else if(!(this.selection.primary || {}).Id && !(this.selection.friendly || {}).Id) {
			this.setPrimarySwitcherButton(null);
		}

		if(!!this.isReady) {
			this._eventBus.trigger(EVENT_VALUE_CHANGED(ctrl.field.Id), {
				field: ctrl.field,
				value: found,
				primary: ctrl.selection.primary
			});
		}
		else {
			this.runOnReady.push(() => this._eventBus.trigger(EVENT_VALUE_CHANGED(ctrl.field.Id), {
				field: ctrl.field,
				value: found,
				primary: ctrl.selection.primary,
				isInitializing: true
			}));
		}

		if(this.field.setEntityTimezone === true && (this.selection.primary?.Id || this.selection.friendly?.Id)) {
			const timezoneCode = this.entityManager.getUniqueTimezoneForEntity(this.field.Id, this.selection.primary?.Id ?? this.selection.friendly?.Id);
			if(timezoneCode === UNIQUE_TIMEZONE_ERRORS.MULTIPLE) {
				notifyMultipleEntityTimezoneError(this.field.Name);
				throw new Error("Selected entity has multiple available timezones in ByEntity configuration");
			}
			else if(timezoneCode === UNIQUE_TIMEZONE_ERRORS.ZERO) {
				notifyNoEntityTimezoneError(this.field.Name);
				throw new Error("Selected entity has no available timezones in ByEntity configuration");
			}

			this._eventBus.trigger(EVENT_TIMEZONE_SET(ctrl.field.Id), { value: timezoneCode });
		}

		this.updateFieldDirty();
	}

	handleCustomInputChanged() {
		const hasLastCustomInputValue = isStringNullishOrEmpty(this.lastCustomInputValue);
		const hasNewValue = isStringNullishOrEmpty(this.value);

		if(hasLastCustomInputValue !== hasNewValue) {
			this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), {
				field: this.field,
				value: this.value
			});
		}
		this.lastCustomInputValue = this.value;

		this.updateFieldDirty();
	}

	updateFieldDirty() {
		const result = this.buildResult();
		const isDirty = this.isDirty(result);

		this.setFieldDirty(this.field.Id, isDirty);
	}

	isDirty(result) {
		const dirty = (this.field.visible !== false || this.initialValue !== undefined)
			&& this.field.IsNotReported !== true
			&& this.field.IsHidden !== true
			&& !this.field.isReadonly
			&& (!this.field.isDisabled || this.isNotAvailable)
			&& (
				(result?.EntityId !== this.initialValue?.EntityId)
				|| (this.isCustomInput && this.initialValue?.String !== result?.String)
				|| (this.isNotAvailable && this.initialValue?.IsNotAvailable !== result?.IsNotAvailable)
			);

		return dirty;
	}

	setPrimarySwitcherButton(entities) {
		this.showPrimarySwitcher = entities && entities.length > 1 && !this.isCustomInput;
	}

	switchPrimary() {
		this.selectBox.switchPrimary();
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}
}

export default angular
	.module("vesl.components.report-form.selection", [])
	.component("selection", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			pivotRow: "=",
			pivotColumn: "=",
			subFieldCtrl: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "?^reportForm",
			reportScope: "?^^reportScope"
		}
	}).name;
